import { useEffect, useMemo, useState } from 'react'
import { useBreakoutUser } from './profile'
import type { Section } from '@breakoutlearning/firebase-repository/models/Section'
import { useRootStore } from './rootStore'
import { BreakoutUser } from '@breakoutlearning/firebase-repository/models/BreakoutUser'
import { useRepository } from './auth'
import {
  APPROVED_FREE_PROMOTION_NAME,
  type UserPromotion,
} from '@breakoutlearning/firebase-repository/models/UserPromotion'

export const useUserPromotions = (section?: Section): ReturnType => {
  const repository = useRepository()
  const { impersonationInfo } = useRootStore()
  const _user = useBreakoutUser()
  const [user, setUser] = useState<BreakoutUser>(_user)

  // set the user to the impersonated user if impersonation is active
  // and dispose on exit/impersonation info update
  useEffect(() => {
    if (impersonationInfo) {
      const impersonatedUser = new BreakoutUser(
        repository,
        impersonationInfo.userId
      )
      impersonatedUser.startUserPromotionRedemptionsStreamIfNotRunning()
      impersonatedUser.startUserPromotionsStreamIfNotRunning()
      setUser(impersonatedUser)
      return () => impersonatedUser.dispose()
    }
    setUser(_user)
    return () => {}
  }, [_user, impersonationInfo, repository])

  const [selectedUserPromotion, setSelectedUserPromotion] = useState<
    string | null
  >(null)

  const [selectedUserPromotionIds, setSelectedUserPromotionIds] = useState<
    string[]
  >([])

  const [applyAdminSpecialPromotion, setApplyAdminSpecialPromotion] =
    useState(false)

  const userPromotionRedemptionsArray = user.userPromotionRedemptions.models
  const userPromotions = user.userPromotions.models
  const userPromotionRedemptions =
    user.userPromotionRedemptionsByUserPromotionId

  const selectedUserPromotionModel = useMemo(() => {
    if (!selectedUserPromotion) return null
    return userPromotions?.find((p) => p.id === selectedUserPromotion) || null
  }, [selectedUserPromotion, userPromotions])

  const redemptionsForSection = useMemo(() => {
    if (!section) return []
    return userPromotionRedemptionsArray.filter(
      (redemption) => redemption.data.sectionId === section.id
    )
  }, [userPromotionRedemptionsArray, section])

  const usedPromotionIds = useMemo(() => {
    const fromRedemptions = redemptionsForSection.map(
      (redemption) => redemption.data.userPromotionId
    )
    return [...fromRedemptions, ...selectedUserPromotionIds]
  }, [redemptionsForSection, selectedUserPromotionIds])

  const promotionIsConsumable = useMemo(() => {
    if (!selectedUserPromotion || !selectedUserPromotionModel) return false
    const redemptions =
      userPromotionRedemptions?.get(selectedUserPromotion) || []
    const { consumableCount } = selectedUserPromotionModel.data
    if (!consumableCount) return false
    const consumedCount = redemptions.length
    return consumedCount < consumableCount
  }, [
    selectedUserPromotion,
    selectedUserPromotionModel,
    userPromotionRedemptions,
  ])

  const userPromotionOptions = useMemo(() => {
    const unusedPromotions = userPromotions?.filter(
      (p) =>
        !usedPromotionIds.includes(p.id) &&
        p.data.promotionName !== APPROVED_FREE_PROMOTION_NAME // don't show special admin promotions in instructor list
    )
    return (
      unusedPromotions?.map((userPromotion) => ({
        label: userPromotion.data.promotionName,
        value: userPromotion.id,
      })) || []
    )
  }, [userPromotions, usedPromotionIds])

  const hasPromotions = !!userPromotions && userPromotions.length > 0

  const returnValue = useMemo(
    () => ({
      user,
      applyAdminSpecialPromotion,
      setApplyAdminSpecialPromotion,
      userPromotions,
      selectedUserPromotion,
      selectedUserPromotionModel,
      selectedUserPromotionIds,
      usedPromotionIds,
      promotionIsActive: selectedUserPromotionModel?.isActive ?? false,
      promotionIsConsumable,
      userPromotionOptions,
      hasPromotions,
      userPromotionRedemptions,
      userPromotionRedemptionsArray,
      setSelectedUserPromotion,
      setSelectedUserPromotionIds,
    }),
    [
      user,
      applyAdminSpecialPromotion,
      setApplyAdminSpecialPromotion,
      userPromotions,
      selectedUserPromotion,
      selectedUserPromotionIds,
      usedPromotionIds,
      promotionIsConsumable,
      userPromotionOptions,
      hasPromotions,
      userPromotionRedemptions,
      userPromotionRedemptionsArray,
      selectedUserPromotionModel,
    ]
  )

  return returnValue
}

type ReturnType = {
  user: BreakoutUser
  applyAdminSpecialPromotion: boolean
  setApplyAdminSpecialPromotion: (value: boolean) => void
  userPromotions: BreakoutUser['userPromotions']['models']
  selectedUserPromotion: string | null
  selectedUserPromotionModel: UserPromotion | null
  selectedUserPromotionIds: string[]
  usedPromotionIds: string[]
  promotionIsActive: boolean
  promotionIsConsumable: boolean
  userPromotionOptions: Array<{ label: string; value: string }>
  hasPromotions: boolean
  userPromotionRedemptions: BreakoutUser['userPromotionRedemptionsByUserPromotionId']
  userPromotionRedemptionsArray: BreakoutUser['userPromotionRedemptions']['models']
  setSelectedUserPromotion: (promotion: string | null) => void
  setSelectedUserPromotionIds: (ids: string[]) => void
}
